import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { usePayslip } from '../api'
import type { PayrollLineView } from '../types'

/** One person's pay for one period, with each figure explainable. */
export function PayslipPanel({
  runId,
  employeeId,
  onExplain,
}: {
  runId: string
  employeeId: string
  onExplain: (lineId: string) => void
}) {
  const payslip = usePayslip(runId, employeeId)

  if (payslip.isPending) return <Skeleton className="h-64 w-full" />
  if (!payslip.data) return null

  const slip = payslip.data

  function section(title: string, lines: PayrollLineView[]) {
    if (lines.length === 0) return null
    return (
      <div>
        <h4 className="mb-1 text-sm font-medium">{title}</h4>
        <table className="w-full text-sm">
          <tbody>
            {lines.map((line) => (
              <tr key={line.id} className="border-b border-slate-100">
                <td className="py-1.5">{line.elementName ?? line.elementCode}</td>
                <td className="py-1.5 text-end font-medium">{line.amount}</td>
                <td className="w-8 py-1.5 text-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Why is ${line.elementName ?? line.elementCode} ${line.amount}`}
                    onClick={() => onExplain(line.id)}
                  >
                    <Search className="size-3.5" aria-hidden="true" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <Card className="space-y-4 p-4">
      <div>
        <h3 className="text-base font-semibold">{slip.employeeName}</h3>
        <p className="text-sm text-slate-600">
          {slip.employeeCode} · {slip.periodStart} to {slip.periodEnd}
        </p>
      </div>

      {section('Earnings', slip.earnings)}
      {section('Deductions', slip.deductions)}
      {section("Employer's cost", slip.employerContributions)}
      {section('For information only', slip.informational)}

      <dl className="grid gap-x-6 gap-y-1 border-t border-slate-200 pt-3 text-sm sm:grid-cols-[10rem_1fr]">
        <dt className="text-slate-600">Gross</dt>
        <dd>{slip.grossPay} {slip.currency}</dd>
        <dt className="text-slate-600">Deductions</dt>
        <dd>{slip.totalDeductions} {slip.currency}</dd>
        <dt className="font-medium">Net pay</dt>
        <dd className="font-medium">{slip.netPay} {slip.currency}</dd>
        <dt className="text-slate-600">Cost to the employer</dt>
        <dd className="text-slate-600">
          {slip.employerCost} {slip.currency} on top of gross
        </dd>
      </dl>
    </Card>
  )
}
